import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import {
  LoginUser,
  LoginGroup,
  LoginUserImis,
} from '../../../api/agwa-api-model';
import styles from './user-edit-tabs-styles.css?inline';
import { doEventWithOverlay } from '../../../utility/ui-overlay';
import { ApiUserMaintenance } from '../../../api/core/api-user-maintenance';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { toErrorMessage } from '../../../api/error-utils';
import { ApiLogin } from '../../../api/core/agwasuite-api-login';
import { WellKnownIds } from '../../../utility/claim-id';
import { terms } from '../../../language/terms';
import { personName } from '../../../utility/person-name';
import { normalizePhoneNumber } from '../../../utility/phone-number';
import './staff-row-expander.js';

@customElement('user-edit-tabs')
export class UserEditTabs extends LitElement {
  static styles = [unsafeCSS(styles)];

  @property({ type: Object }) group: LoginGroup | null = null;
  @property({ type: Object }) user: LoginUser | null = null;
  @property({ type: Object }) imis: LoginUserImis | null = null;
  @property({ type: Boolean }) loading = false;
  @property({ type: Boolean }) open = false;
  @property({ type: String }) currentUsername = '';

  @state() private displayName = '';
  @state() private emailAddress = '';
  /** The person's phone number as typed, seeded from the stored one (D-151 rule 2). */
  @state() private phoneNumber = '';
  @state() private username = '';
  @state() private usernamePrefix = '';
  @state() private usernameValue = '';
  @state() private lockInformEmail = false;
  @state() private activateInformEmail = false;
  @state() private imisReference = '';

  // Loading and success states for security actions
  @state() private resetLoading = false;
  @state() private resetSuccess = false;
  @state() private lockLoading = false;
  @state() private lockSuccess = false;
  @state() private activateLoading = false;
  @state() private activateSuccess = false;
  @state() private saveError: string | null = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.user) {
      this.displayName = this.user.displayName;
      this.emailAddress = this.user.emailAddress;
      this.phoneNumber = this.user.phoneNumber ?? '';
      this.username = this.user.username;
      this.updateUsernameFields();
    }
    if (this.imis) {
      this.imisReference = this.imis.reference;
    }
  }

  /** Returns true if this group is an internal (AGWA) group - seeded system tenant */
  private get isInternalGroup(): boolean {
    return this.group?.id?.toLowerCase() === WellKnownIds.agwaGroupId;
  }

  private updateUsernameFields() {
    if (!this.group || !this.username) return;

    // Get the prefix from the group
    const prefix = this.group.userNamePrefix
      ? `${this.group.userNamePrefix}.`
      : '';

    // Check if the username starts with the prefix
    if (prefix && this.username.startsWith(prefix)) {
      this.usernamePrefix = prefix;
      this.usernameValue = this.username.substring(prefix.length);
    } else {
      // If no prefix match, use a default behavior (keeping this as a fallback)
      const lastDotIndex = this.username.indexOf('.');
      if (lastDotIndex > 0) {
        this.usernamePrefix = this.username.substring(0, lastDotIndex + 1);
        this.usernameValue = this.username.substring(lastDotIndex + 1);
      } else {
        this.usernamePrefix = '';
        this.usernameValue = this.username;
      }
    }
  }
  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('user') && this.user) {
      this.displayName = this.user.displayName;
      this.emailAddress = this.user.emailAddress;
      this.phoneNumber = this.user.phoneNumber ?? '';
      this.username = this.user.username;
      this.updateUsernameFields();
    }
    if (changedProps.has('group') && this.group) {
      this.updateUsernameFields();
    }
    // A re-read of the person while the page stays open brings a new iMIS record; the field follows it as the other fields follow the user, including
    // its absence: a record that is gone leaves no reference behind (#398 C14 b). The field is not
    // drawn and nothing is sent without a record (getUpdatedImis), so this keeps the state true to
    // the record rather than changing anything a person sees.
    if (changedProps.has('imis')) {
      this.imisReference = this.imis?.reference ?? '';
    }
  }

  private handleDisplayNameChange(e: CustomEvent) {
    this.displayName = (e.target as HTMLInputElement).value;
  }

  private handleEmailChange(e: CustomEvent) {
    this.emailAddress = (e.target as HTMLInputElement).value;
  }

  private handlePhoneNumberChange(e: CustomEvent) {
    this.phoneNumber = (e.target as HTMLInputElement).value;
  }

  private handleUsernameChange(e: CustomEvent) {
    this.usernameValue = (e.target as HTMLInputElement).value;
    this.username = this.usernamePrefix + this.usernameValue;
  }

  private handleLockInformEmailChange(e: CustomEvent) {
    this.lockInformEmail = (e.target as HTMLInputElement).checked;
  }

  private handleActivateInformEmailChange(e: CustomEvent) {
    this.activateInformEmail = (e.target as HTMLInputElement).checked;
  }

  private handleImisReferenceChange(e: CustomEvent) {
    const value = (e.target as HTMLInputElement).value;
    if (value.length <= 100) {
      this.imisReference = value;
    }
  }

  private getUpdatedImis(): LoginUserImis | null {
    if (!this.imis) return null;
    return {
      ...(this.imis ?? {}),
      reference: this.imisReference,
      loginUserId: this.user?.id ?? '',
      properties: this.imis?.properties ?? {},
    };
  }

  private onSave() {
    this.saveError = null;
    // The phone rule is applied before anything is sent (D-151 rule 3), in the server's own words.
    const phone = normalizePhoneNumber(this.phoneNumber);
    if (phone.error) {
      this.saveError = phone.error;
      return;
    }
    const updatedImis = this.getUpdatedImis();
    void doEventWithOverlay(async () => {
      const api = ApiFactory.authenticated(ApiUserMaintenance);

      if (!this.user) return;

      const updatedUser: LoginUser = {
        ...this.user,
        displayName: this.displayName,
        emailAddress: this.emailAddress,
        // Full-state, like the email: the number sent is the number after the save, and none
        // (trimmed to empty) clears it.
        phoneNumber: phone.value ?? undefined,
        username: this.username,
      };

      try {
        const result = await api.editGroupUser(updatedUser, updatedImis);
        if (result.success) {
          // Update local user data
          this.dispatchEvent(
            new CustomEvent('save', {
              detail: result.data.user,
              bubbles: true,
              composed: true,
            })
          );
        } else {
          this.saveError =
            toErrorMessage(result) ||
            `Failed to save ${terms.loginUser.lower} changes.`;
        }
      } catch (err: unknown) {
        this.saveError =
          (err as Error)?.message || 'An unexpected error occurred.';
      }
    });
  }

  private onCancel() {
    this.dispatchEvent(
      new CustomEvent('cancel', { bubbles: true, composed: true })
    );
  }

  private async forcePasswordReset() {
    this.resetLoading = true;
    this.resetSuccess = false;

    try {
      void doEventWithOverlay(async () => {
        const api = ApiFactory.authenticated(ApiLogin);

        const result = await api.requestPasswordReset({
          loginUserId: this.user?.id,
        });

        if (result.success) {
          // Deliberately does NOT touch `locked` or `lockSuccess`. It used to set both, claiming an
          // action it had not performed: JobRequestPasswordReset never reads or writes Locked, so a
          // reset forces a new password on next sign-in and does not withdraw access.
          //
          // Both writes were dead rather than harmful - the host reloads the user on the event
          // dispatched just below, replacing this object before either could be seen. Verified by
          // reinstating them: no assertion on the alert, the staff list, or this panel's own
          // Block/Activate controls could tell the two versions apart. Removed because state that
          // lies about what happened is one refactor away from being visible, not because anything
          // observable was wrong.
          this.dispatchEvent(
            new CustomEvent('user-password-reset', {
              detail: this.user,
              bubbles: true,
              composed: true,
            })
          );
        }
      });
      this.resetSuccess = true;
      this.dispatchEvent(
        new CustomEvent('password-reset', {
          detail: {
            userId: this.user?.id,
          },
          bubbles: true,
          composed: true,
        })
      );
    } catch (error) {
      console.error('Error resetting password', error);
    } finally {
      this.resetLoading = false;
      setTimeout(() => (this.resetSuccess = false), 2000);
    }
  }

  /**
   * After this page's own Block or Activate, takes on the person as the server now holds them, in
   * place, so the unsaved edits in the fields stay (#398 C17b, the SA's ruling on C18 review 1 F3).
   * Whether they are blocked always follows. The version (concurrencyToken) is taken only when the
   * details the server returned are the ones this page loaded: then the block is the only change
   * since it opened, and the next details save goes through. When they differ, a colleague saved the
   * person after this page opened, and the version it loaded is kept, so the next save is refused as
   * changed since it opened (@LOCK-057) instead of quietly putting their details back. The iMIS
   * record keeps its own loaded version either way: a block does not touch it, and the save compares
   * it on its own (C18 review 1 F1).
   */
  private adoptBlockResult(stored: LoginUser, blocked: boolean) {
    const loaded = this.user;
    if (!loaded) return;
    const sameDetails =
      loaded.username === stored.username &&
      loaded.displayName === stored.displayName &&
      loaded.emailAddress === stored.emailAddress &&
      (loaded.phoneNumber ?? null) === (stored.phoneNumber ?? null);
    loaded.locked = blocked;
    if (sameDetails) {
      loaded.concurrencyToken = stored.concurrencyToken;
    }
    this.requestUpdate();
  }

  private async lockUser() {
    if (!this.user) return;

    this.lockLoading = true;
    // A refusal from an earlier action is cleared; this one says its own.
    this.saveError = null;
    this.lockSuccess = false;

    const loginUserId = this.user.id;
    try {
      void doEventWithOverlay(async () => {
        const api = ApiFactory.authenticated(ApiUserMaintenance);

        // Only whether the person is blocked changes (#398 C18, @LOCK-059): the page's copy of
        // their details, and its unsaved edits, are not sent, so a Block pressed on a page opened
        // before somebody else saved the person cannot put the old details back.
        const result = await api.setGroupUserBlocked({
          loginUserId,
          blocked: true,
          informUser: this.lockInformEmail,
        });
        if (result.success) {
          this.adoptBlockResult(result.data.user, true);

          this.lockSuccess = true;
          this.dispatchEvent(
            new CustomEvent('user-locked', {
              detail: result.data.user,
              bubbles: true,
              composed: true,
            })
          );
        } else {
          // A refusal is said on screen, above the tabs as a save's is: a 403, an unknown person, or
          // a 409 when a colleague's save landed while this one ran (D-164), so the person knows the
          // user was not blocked (C18 review 1 F2).
          this.saveError =
            toErrorMessage(result) ||
            `The ${terms.loginUser.lower} could not be blocked.`;
        }
      });
    } catch (error) {
      console.error('Error locking user', error);
    } finally {
      this.lockLoading = false;
      setTimeout(() => (this.lockSuccess = false), 2000);
    }
  }
  private async activateUser() {
    if (!this.user) return;

    this.activateLoading = true;
    // A refusal from an earlier action is cleared; this one says its own.
    this.saveError = null;
    this.activateSuccess = false;

    const loginUserId = this.user.id;
    try {
      void doEventWithOverlay(async () => {
        const api = ApiFactory.authenticated(ApiUserMaintenance);

        // Only whether the person is blocked changes (#398 C18, @LOCK-059); as Block.
        const result = await api.setGroupUserBlocked({
          loginUserId,
          blocked: false,
          informUser: this.activateInformEmail,
        });
        if (result.success) {
          this.adoptBlockResult(result.data.user, false);

          // Activating reports the ACTIVATION. This set the lock flag instead, so activating a
          // blocked user told them "User blocked successfully" - the opposite of what had just
          // happened - and the message never cleared, because the timeout below resets
          // activateSuccess, which nothing had set.
          this.activateSuccess = true;
          this.dispatchEvent(
            new CustomEvent('user-activated', {
              detail: result.data.user,
              bubbles: true,
              composed: true,
            })
          );
        } else {
          // A refusal is said on screen; as Block.
          this.saveError =
            toErrorMessage(result) ||
            `The ${terms.loginUser.lower} could not be activated.`;
        }
      });
    } catch (error) {
      console.error('Error activating user', error);
    } finally {
      this.activateLoading = false;
      setTimeout(() => (this.activateSuccess = false), 2000);
    }
  }

  render() {
    if (!this.user) return null;
    const isCurrentUser =
      this.currentUsername && this.user.username === this.currentUsername;
    const isAdminUser = this.user.username.toLowerCase() === 'admin';

    const imisEditTemplate = this.imis
      ? html`
          <sl-input
            label="${terms.imis.value} Reference"
            value=${this.imisReference}
            maxlength="100"
            @sl-input=${this.handleImisReferenceChange}
            placeholder="Enter ${terms.imis.value} reference"
          ></sl-input>
        `
      : null;
    return html`
      <div class="user-edit-tabs-main">
        ${
          this.saveError
            ? html`<sl-alert variant="danger" open>${this.saveError}</sl-alert>`
            : null
        }
        <sl-tab-group class="user-edit-tabs-tabgroup">
          <sl-tab slot="nav" panel="details">Details</sl-tab>
          <sl-tab slot="nav" panel="security">Security</sl-tab>
          ${
            !this.isInternalGroup
              ? html`<sl-tab slot="nav" panel="sites">Sites</sl-tab>`
              : null
          }

          <sl-tab-panel name="details" class="edit-user-tab-panel">
            <form class="edit-user-form">
              <sl-input
                label="Username"
                value=${this.usernameValue}
                @sl-input=${this.handleUsernameChange}
                ?disabled=${isAdminUser}
                help-text="Username is prefixed with the ${terms.loginGroup.lower} identifier."
              >
                <span
                  slot="prefix"
                  class="group-prefix-label"
                  aria-label="${terms.loginGroup.value} Prefix"
                  >${this.usernamePrefix}</span
                >
              </sl-input>
              <sl-input
                label="Display Name"
                value=${this.displayName}
                @sl-input=${this.handleDisplayNameChange}
              ></sl-input>
              <sl-input
                label="Email"
                value=${this.emailAddress}
                @sl-input=${this.handleEmailChange}
              ></sl-input>
              <!-- Under Email, optional (D-151 rule 2). The rule's refusal shows as it is typed. -->
              <sl-input
                label="Phone"
                type="tel"
                value=${this.phoneNumber}
                help-text=${normalizePhoneNumber(this.phoneNumber).error ?? ''}
                @sl-input=${this.handlePhoneNumberChange}
              ></sl-input>
              ${imisEditTemplate}
            </form>
            <div class="edit-user-footer">
              <sl-button variant="neutral" @click=${this.onCancel}
                >Cancel</sl-button
              >
              <sl-button variant="primary" @click=${this.onSave}
                >Save</sl-button
              >
            </div>
          </sl-tab-panel>
          <sl-tab-panel name="security" class="edit-user-tab-panel">
            <div class="edit-user-security-panel">
              ${
                this.resetSuccess
                  ? html`
                      <!--
                        Both halves of what just happened, because only one of them is visible:
                        an operator can see that an email went out, but nothing on screen shows
                        that the password the person already had has stopped working (D-017).
                      -->
                      <sl-alert variant="success" open>
                        Password reset successful. The new password was emailed
                        to the ${terms.loginUser.lower}, and their current
                        password no longer works.
                      </sl-alert>
                    `
                  : null
              }
              ${
                this.lockSuccess
                  ? html`
                      <sl-alert variant="success" open>
                        ${terms.loginUser.value} blocked
                        successfully${
                          this.lockInformEmail
                            ? '. Notification email sent.'
                            : ''
                        }
                      </sl-alert>
                    `
                  : null
              }
              ${
                this.activateSuccess
                  ? html`
                      <sl-alert variant="success" open>
                        ${terms.loginUser.value} activated
                        successfully${
                          this.activateInformEmail
                            ? '. Notification email sent.'
                            : ''
                        }
                      </sl-alert>
                    `
                  : null
              }

              <div class="edit-user-security-row">
                <sl-button
                  variant="danger"
                  @click=${this.forcePasswordReset}
                  ?loading=${this.resetLoading}
                  ?disabled=${this.resetLoading}
                >
                  Force Password Reset
                </sl-button>
                <!--
                  A statement, not a control: the reset is always emailed, so there is no choice to
                  offer. It was a permanently disabled, permanently ticked checkbox, which reads as
                  something the viewer is forbidden to use rather than as a promise the system keeps.

                  It says what the button DOES before it is pressed, both halves. An operator
                  reaches for this when an account may be in the wrong hands, and whether it shuts
                  the current holder out is the thing they need to know at that moment - not after
                  the act. D-017.
                -->
                <span class="edit-user-security-note">
                  <sl-icon name="envelope-fill"></sl-icon>
                  The new password is emailed to the ${terms.loginUser.lower},
                  and their current password stops working
                </span>
              </div>
              ${
                this.user && !this.user.locked && !isCurrentUser
                  ? html`
                      <div class="edit-user-security-row">
                        <sl-button
                          variant="warning"
                          @click=${this.lockUser}
                          ?loading=${this.lockLoading}
                          ?disabled=${this.lockLoading}
                        >
                          Block ${terms.loginUser.value}
                        </sl-button>
                        <sl-checkbox
                          ?checked=${this.lockInformEmail}
                          @sl-change=${this.handleLockInformEmailChange}
                          class="edit-user-security-checkbox"
                        >
                          Inform ${terms.loginUser.lower} by email
                        </sl-checkbox>
                      </div>
                    `
                  : null
              }
              ${
                this.user && this.user.locked
                  ? html`
                      <div class="edit-user-security-row">
                        <sl-button
                          variant="success"
                          @click=${this.activateUser}
                          ?loading=${this.activateLoading}
                          ?disabled=${this.activateLoading}
                        >
                          Activate ${terms.loginUser.value}
                        </sl-button>
                        <sl-checkbox
                          ?checked=${this.activateInformEmail}
                          @sl-change=${this.handleActivateInformEmailChange}
                          class="edit-user-security-checkbox"
                        >
                          Inform ${terms.loginUser.lower} by email
                        </sl-checkbox>
                      </div>
                    `
                  : null
              }
            </div>
          </sl-tab-panel>
          ${
            !this.isInternalGroup && this.user && this.group
              ? html`
                  <sl-tab-panel name="sites" class="edit-user-tab-panel">
                    <staff-row-expander
                      .userId=${this.user.id}
                      .groupId=${this.group.id}
                      .userName=${personName(this.user.displayName)}
                    ></staff-row-expander>
                  </sl-tab-panel>
                `
              : null
          }
        </sl-tab-group>
      </div>
    `;
  }
}
